import Renderer from 'apollo-e/rendering/Renderer';
import Object3D from 'apollo-e/rendering/Object3D';
import { Shader, VertexShader, FragmentShader } from 'apollo-e/rendering/Shader';
import { TextureSet, EMPTY_TEXTURE_SET } from 'apollo-e/rendering/Texture';

import Vector2 from 'apollo-e/math/Vector2';
import Vector3 from 'apollo-e/math/Vector3';
// import Transform3D from 'apollo-e/transformNodes/Transform3D';

import opacities from 'apollo-e/rendering/OPACITIES';

const defaultVertexScript = require('./shaders/vertex/qt.vert');
const defaultFragmentScript = require('./shaders/fragment/qt.frag');

const shadedVertexScript = require('./shaders/vertex/qtShaded.vert');
const shadedFragmentScript = require('./shaders/fragment/qtShaded.frag');

const defaultVertexShader = new VertexShader(defaultVertexScript, ['aPosition', 'aRotation', 'aGreyShade', 'aOffset', 'aUV'], ['uMMatrix', 'uCameraPosition']);
const defaultFragmentShader = new FragmentShader(defaultFragmentScript, ['uAmbientLight', 'uDirectionalLight', 'uLightDirection'], ['uTexture']);
const defaultShader = new Shader(defaultVertexShader, defaultFragmentShader);

const shadedVertexShader = new VertexShader(shadedVertexScript, ['aPosition', 'aRotation', 'aGreyShade', 'aOffset', 'aUV'], ['uMMatrix', 'uCameraPosition', 'uShadowMapVPMatrix']);
const shadedFragmentShader = new FragmentShader(shadedFragmentScript, ['uAmbientLight', 'uDirectionalLight', 'uLightDirection'], ['uTexture', 'uShadowMap']);
const shadedShader = new Shader(shadedVertexShader, shadedFragmentShader);

const shaders = {
	default: defaultShader,
	shaded: shadedShader
};

class Particle3D {
	width: number;
	height: number;
	position: Vector3;
	rotation: number;
	greyShade: number;
	scales: Vector2;
	pivots: Vector2;
	textureSet: TextureSet;
	payload: any;

	constructor () {
		this.width = 1;
		this.height = 1;
		this.position = new Vector3(0, 0, 0);
		this.rotation = 0;
		this.scales = new Vector2(1, 1);
		this.pivots = new Vector2(0, 0);
		this.greyShade = 1;
		this.textureSet = EMPTY_TEXTURE_SET;
		this.payload = null;
	}

	get scale () {
		return (this.scales.x + this.scales.y) / 2;
	}

	set scale (scale) {
		this.scales.x = scale;
		this.scales.y = scale;
	}

	get pivot () {
		return (this.pivots.x / this.width + this.pivots.y / this.height) / 2;
	}

	set pivot (pivot) {
		this.pivots.x = pivot * this.width;
		this.pivots.y = pivot * this.height;
	}
}

const populateBuffer = {
	position: function (particles: Array<Particle3D>, firstParticle: number, floatView: Float32Array, offset: number, stride: number) {
		// offset /= 4;
		for (let p = firstParticle; p < particles.length; p++) {
			const particle = particles[p];
			const position = particle.position;
			floatView[offset + 0] = floatView[offset + stride + 0] = floatView[offset + stride * 2 + 0] = floatView[offset + stride * 3 + 0] = position.x;
			floatView[offset + 1] = floatView[offset + stride + 1] = floatView[offset + stride * 2 + 1] = floatView[offset + stride * 3 + 1] = position.y;
			floatView[offset + 2] = floatView[offset + stride + 2] = floatView[offset + stride * 2 + 2] = floatView[offset + stride * 3 + 2] = position.z;
			offset += stride * 4;
		}
	},

	rotation: function (particles: Array<Particle3D>, firstParticle: number, floatView: Float32Array, offset: number, stride: number) {
		// offset /= 4;
		for (let p = firstParticle; p < particles.length; p++) {
			const particle = particles[p];
			floatView[offset + 0] = floatView[offset + stride] = floatView[offset + stride * 2] = floatView[offset + stride * 3] = particle.rotation;
			offset += stride * 4;
		}
	},

	greyShade: function (particles: Array<Particle3D>, firstParticle: number, floatView: Float32Array, offset: number, stride: number) {
		// offset /= 4;
		for (let p = firstParticle; p < particles.length; p++) {
			const particle = particles[p];
			floatView[offset + 0] = floatView[offset + stride] = floatView[offset + stride * 2] = floatView[offset + stride * 3] = particle.greyShade;
			offset += stride * 4;
		}
	},

	vertices: function (particles: Array<Particle3D>, firstParticle: number, floatView: Float32Array, offset: number, stride: number) {
		// offset /= 4;
		for (let p = firstParticle; p < particles.length; p++) {
			const particle = particles[p];

			const scales = particle.scales;
			const scaleX = scales.x;
			const scaleY = scales.y;
	
			const pivots = particle.pivots;
			const x0 = -pivots.x * scaleX;
			const y0 = -pivots.y * scaleY;
	
			const x1 = x0 + particle.width * scaleX;
			const y1 = y0 + particle.height * scaleY;
	
			floatView[offset + 0] = x0;
			floatView[offset + 1] = y0;
			floatView[offset + stride + 0] = x1;
			floatView[offset + stride + 1] = y0;
			floatView[offset + stride * 2 + 0] = x0;
			floatView[offset + stride * 2 + 1] = y1;
			floatView[offset + stride * 3 + 0] = x1;
			floatView[offset + stride * 3 + 1] = y1;
			offset += stride * 4;
		}
	},

	uvs: function (particles: Array<Particle3D>, firstParticle: number, uLongView: Uint32Array, offset: number, stride: number) {
		// offset /= 4;
		for (let p = firstParticle; p < particles.length; p++) {
			const particle = particles[p];

			const uvs = particle.textureSet.coordinates[0];

			uLongView[offset + 0] = uvs.bottomLeft;
			uLongView[offset + stride] = uvs.bottomRight;
			uLongView[offset + stride * 2] = uvs.topLeft;
			uLongView[offset + stride * 3] = uvs.topRight;
			offset += stride * 4;
		}
	},

};

class DynamicProperties {
	position: boolean;
	rotation: boolean;
	vertices: boolean;
	greyShade: boolean;
	uvs: boolean;

	constructor (dynamicProperties: Array<string>) {
		this.position = false;
		this.rotation = false;
		this.vertices = false;
		this.uvs = false;
		this.greyShade = false;

		for (let p = 0; p < dynamicProperties.length; p++) {
			this[dynamicProperties[p]] = true;
		}
	}
}

class Attribute {
	name: string;
	count: number;
	type: number;
	normalize: boolean;
	byteSize: number;
	byteStride: number;

	constructor (name: string, count: number, normalize: boolean, byteSize: number) {
		this.name = name;
		this.count = count;
		this.normalize = normalize;
		this.byteSize = byteSize;
		this.byteStride = -1;
		this.type = -1;
	}
}

const defaultMaxBatchSize = 4096;

export default class QTContainer3D extends Object3D {
	shaders = shaders;

	particles: Array<Particle3D>;
	private _particleUpdateOffset: number;
	private _buffers: { [property: string]: Float32Array | Uint32Array };
	private _bufferBinders: Array<{ [property: string]: WebGLBuffer }>;
	private _attributes: { [property: string]: Attribute };
	private _dynamicProperties: DynamicProperties;
	private _dynamicBuffer: ArrayBuffer;
	private _staticBuffer: ArrayBuffer;
	private _dynamicBufferView: Float32Array;
	private _staticBufferView: Float32Array;
	private _dynamicStride: number;
	private _staticStride: number;
	private _maxBatchSize: number;

	constructor (dynamicProperties: Array<string>, maxBatchSize: number = defaultMaxBatchSize) {
		super();
		this.opacity = opacities.OPAQUE;
		this.castShadow = false;

		this._particleUpdateOffset = 0;
		this.particles = [];

		this._dynamicProperties = new DynamicProperties(dynamicProperties);

		this._bufferBinders = [];

		this._attributes = {
			position: new Attribute('aPosition', 3, false, 4 * 3),
			rotation: new Attribute('aRotation', 1, false, 4),
			vertices: new Attribute('aOffset', 2, false, 4 * 2),
			greyShade: new Attribute('aGreyShade', 1, false, 4),
			uvs: new Attribute('aUV', 2, true, 4),
		};

		this._dynamicStride = 0;
		this._staticStride = 0;
		for (let property in this._dynamicProperties) {
			const attribute = this._attributes[property];
			if (this._dynamicProperties[property] === true) {
				attribute.byteStride = this._dynamicStride;
				this._dynamicStride += attribute.byteSize;
			} else {
				attribute.byteStride = this._staticStride;
				this._staticStride += attribute.byteSize;
			}
		}

		this._maxBatchSize = maxBatchSize;
		const maxVertexCount = 4 * maxBatchSize;
		this._staticBuffer = new ArrayBuffer(maxVertexCount * this._staticStride);
		this._dynamicBuffer = new ArrayBuffer(maxVertexCount * this._dynamicStride);
		this._dynamicBufferView = new Float32Array(this._dynamicBuffer);
		this._staticBufferView = new Float32Array(this._staticBuffer);

		const ArrayBufferTypes = {
			position: Float32Array,
			rotation: Float32Array,
			vertices: Float32Array,
			greyShade: Float32Array,
			uvs: Uint32Array,
		};

		this._buffers = {};
		for (let property in this._dynamicProperties) {
			if (this._dynamicProperties[property] === true) {
				this._buffers[property] = new ArrayBufferTypes[property](this._dynamicBuffer);
			} else {
				this._buffers[property] = new ArrayBufferTypes[property](this._staticBuffer);
			}
		}

		// this._buffers = {
		// 	// position: new Float32Array(maxVertexCount * 3),
		// 	// rotation: new Float32Array(maxVertexCount),
		// 	// vertices: new Float32Array(maxVertexCount * 2),
		// 	// uvs: new Uint32Array(maxVertexCount),
		// 	// alpha: new Float32Array(maxVertexCount),
		// };
	}

	createParticle () {
		if (this.particles.length >= this._maxBatchSize) {
			throw new Error('Could not create new particle: max particle count reach');
		}

		const particle = new Particle3D();
		particle.textureSet = this.textureSet;
		this.particles.push(particle);
		return particle;
	}

	removeParticle (particle: Particle3D) {
		const index = this.particles.indexOf(particle);
		if (index !== -1) {
			this.particles.splice(index, 1);
			this._particleUpdateOffset = Math.min(this._particleUpdateOffset, index);
		}
	}

	private _initializeBufferBinders (gl: WebGLRenderingContext) {
		const staticBufferBinder = gl.createBuffer();
		gl.bindBuffer(gl.ARRAY_BUFFER, staticBufferBinder);
		gl.bufferData(gl.ARRAY_BUFFER, this._staticBuffer, gl.STATIC_DRAW);

		const dynamicBufferBinder = gl.createBuffer();
		gl.bindBuffer(gl.ARRAY_BUFFER, dynamicBufferBinder);
		gl.bufferData(gl.ARRAY_BUFFER, this._dynamicBuffer, gl.DYNAMIC_DRAW);

		if (staticBufferBinder === null || dynamicBufferBinder === null) {
			throw new Error('Failed to initalize buffers');
		}

		this._attributes.position.type = gl.FLOAT;
		this._attributes.rotation.type = gl.FLOAT;
		this._attributes.vertices.type = gl.FLOAT;
		this._attributes.greyShade.type = gl.FLOAT;
		this._attributes.uvs.type = gl.UNSIGNED_SHORT;

		return {
			static: staticBufferBinder,
			dynamic: dynamicBufferBinder,
		};
	}

	render (renderer: Renderer, shader: Shader) {
		const gl = renderer.gl;

		if (this.particles.length === 0) {
			return;
		}

		let bufferBinders = this._bufferBinders[renderer.id];
		if (bufferBinders === undefined) {
			this._bufferBinders[renderer.id] = bufferBinders = this._initializeBufferBinders(gl);
		}

		const program = shader.program;
		const attributes = program.attributes;

		// uploading static particles
		gl.bindBuffer(gl.ARRAY_BUFFER, bufferBinders.static);
		for (let property in this._dynamicProperties) {
			if (this._dynamicProperties[property] === false) {
				const attribute = this._attributes[property];
				if (this._particleUpdateOffset < this.particles.length) {
					const buffer = this._buffers[property];
					populateBuffer[property](this.particles, this._particleUpdateOffset, buffer, this._particleUpdateOffset * this._staticStride + attribute.byteStride / 4, this._staticStride / 4);
				}
				gl.vertexAttribPointer(attributes[attribute.name], attribute.count, attribute.type, attribute.normalize, this._staticStride, attribute.byteStride);
			}
		}

		if (this._particleUpdateOffset < this.particles.length) {
			const staticLongStride = this._staticStride * 4 / 4;
			gl.bufferSubData(gl.ARRAY_BUFFER, this._particleUpdateOffset * staticLongStride * 4, this._staticBufferView.subarray(this._particleUpdateOffset * staticLongStride, this.particles.length * staticLongStride));
			this._particleUpdateOffset = this.particles.length;
		}

		gl.bindBuffer(gl.ARRAY_BUFFER, bufferBinders.dynamic);
		for (let property in this._dynamicProperties) {
			if (this._dynamicProperties[property] === true) {
				const attribute = this._attributes[property];
				const buffer = this._buffers[property];
				populateBuffer[property](this.particles, 0, buffer, 0 * this._dynamicStride + attribute.byteStride / 4, this._dynamicStride / 4);
				gl.vertexAttribPointer(attributes[attribute.name], attribute.count, attribute.type, attribute.normalize, this._dynamicStride, attribute.byteStride);
			}
		}

		if (this._dynamicStride > 0) {
			gl.bufferSubData(gl.ARRAY_BUFFER, 0, this._dynamicBufferView.subarray(0, this.particles.length * this._dynamicStride * 4 / 4));
		}

		gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, renderer.quadIndexBuffer);
		gl.drawElements(gl.TRIANGLES, 6 * this.particles.length, gl.UNSIGNED_SHORT, 0);
	}

	renderDefault (renderer: Renderer) {
		this.render(renderer, defaultShader);
	}

	renderShaded (renderer: Renderer) {
		this.render(renderer, shadedShader);
	}
}
